import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  query,
  where,
  Timestamp,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Word, Category, StudySet, SupportedLanguage, WordType, NounInfo, VerbInfo, AdjectiveInfo, NativeLanguage, TranslationDirection, HebrewLevel, LearningGoal, StreakData, ReviewRating } from "@/types";
import { analyzeWordGrammar, GrammarAnalysis } from "@/hooks/useDirectApi";
import { createEmptyCard, FSRS, generatorParameters, Rating, type Card, State } from "ts-fsrs";

// FSRS scheduler instance (shared across all calls)
const fsrsParams = generatorParameters({ request_retention: 0.9 });
const fsrs = new FSRS(fsrsParams);

// Grammar schema version - bump this when grammar data structure changes
// Version 1: Initial grammar (conjugations, declensions)
// Version 2: Added prepositions with usage examples
export const GRAMMAR_SCHEMA_VERSION = 2;

// ===========================================
// Words
// ===========================================

// Subcollection reference helpers — data lives under users/{userId}/
function userWordsCol(userId: string) {
  return collection(db, "users", userId, "words");
}
function userWordDoc(userId: string, wordId: string) {
  return doc(db, "users", userId, "words", wordId);
}
function userCategoriesCol(userId: string) {
  return collection(db, "users", userId, "categories");
}
function userCategoryDoc(userId: string, categoryId: string) {
  return doc(db, "users", userId, "categories", categoryId);
}
function userStudySetsCol(userId: string) {
  return collection(db, "users", userId, "studySets");
}
function userStudySetDoc(userId: string, studySetId: string) {
  return doc(db, "users", userId, "studySets", studySetId);
}

/**
 * Strip niqqud (Hebrew vowel marks) for normalization
 */
function stripNiqqud(text: string): string {
  return text.replace(/[\u0591-\u05C7]/g, "").trim();
}

/**
 * Add a word to the user's word bank (without grammar - use addWordWithGrammar instead)
 */
export async function addWord(
  userId: string,
  hebrewText: string,
  translation: string,
  translationLang: SupportedLanguage,
  categories: string[],
  transliteration?: string
): Promise<string> {
  return addWordWithGrammar(userId, hebrewText, translation, translationLang, categories, undefined, transliteration);
}

/**
 * Add a word to the user's word bank with grammar info included
 */
export async function addWordWithGrammar(
  userId: string,
  hebrewText: string,
  translation: string,
  translationLang: SupportedLanguage,
  categories: string[],
  grammar?: {
    wordType: WordType;
    nounInfo?: NounInfo;
    verbInfo?: VerbInfo;
    adjectiveInfo?: AdjectiveInfo;
  },
  transliteration?: string
): Promise<string> {
  const normalizedText = stripNiqqud(hebrewText);

  // Check if word already exists
  const existingQuery = query(
    userWordsCol(userId),
    where("hebrew.normalizedText", "==", normalizedText),
    where("deleted", "==", false)
  );
  const existing = await getDocs(existingQuery);

  if (!existing.empty) {
    // Update existing word
    const existingDoc = existing.docs[0];
    const existingData = existingDoc.data() as Word;

    // Merge categories
    const mergedCategories = [...new Set([...existingData.categories, ...categories])];

    const updates: Record<string, any> = {
      lookupCount: increment(1),
      lastLookupAt: Timestamp.now(),
      categories: mergedCategories,
    };

    // Only update translation if not verified
    if (!existingData.translations[translationLang]?.verified) {
      updates[`translations.${translationLang}`] = {
        text: translation,
        source: "user",
        verified: false,
      };
    }

    // Update grammar if provided and word doesn't have it yet
    if (grammar && !existingData.wordType) {
      updates.wordType = grammar.wordType;
      updates.grammarSchemaVersion = GRAMMAR_SCHEMA_VERSION;
      if (grammar.nounInfo) updates.nounInfo = grammar.nounInfo;
      if (grammar.verbInfo) updates.verbInfo = grammar.verbInfo;
      if (grammar.adjectiveInfo) updates.adjectiveInfo = grammar.adjectiveInfo;
    }

    // Update transliteration if provided and word doesn't have it yet
    if (transliteration && !existingData.hebrew.transliteration) {
      updates["hebrew.transliteration"] = transliteration;
    }

    await updateDoc(existingDoc.ref, updates);

    // Ensure new categories exist and increment their counts
    const newCategories = categories.filter((c) => !existingData.categories.includes(c));
    for (const cat of newCategories) {
      await ensureCategory(userId, cat);
      await incrementCategoryCount(userId, cat, 1);
    }

    return existingDoc.id;
  }

  // Create new word with grammar included
  const now = Timestamp.now();
  const newWord: Record<string, any> = {
    hebrew: {
      text: hebrewText.trim(),
      normalizedText,
      rtl: true,
      ...(transliteration && { transliteration }),
    },
    translations: {
      [translationLang]: {
        text: translation,
        source: "user",
        verified: false,
      },
    },
    lookupCount: 1,
    firstAddedAt: now,
    lastLookupAt: now,
    lastReviewedAt: null,
    reviewStats: {
      knownCount: 0,
      unknownCount: 0,
      lastResult: null,
      priorityScore: 0.5,
      stability: 0,
      difficulty: 0,
      elapsedDays: 0,
      scheduledDays: 0,
      reps: 0,
      lapses: 0,
      state: 0, // State.New
      nextReviewAt: null,
      lastReview: null,
    },
    categories,
    relatedWordIds: [],
    notes: "",
    deleted: false,
  };

  // Include grammar if provided
  if (grammar) {
    newWord.wordType = grammar.wordType;
    newWord.grammarSchemaVersion = GRAMMAR_SCHEMA_VERSION;
    if (grammar.nounInfo) newWord.nounInfo = grammar.nounInfo;
    if (grammar.verbInfo) newWord.verbInfo = grammar.verbInfo;
    if (grammar.adjectiveInfo) newWord.adjectiveInfo = grammar.adjectiveInfo;
  }

  const docRef = await addDoc(userWordsCol(userId), newWord);

  // Ensure categories exist and increment their counts
  for (const cat of categories) {
    await ensureCategory(userId, cat);
    await incrementCategoryCount(userId, cat, 1);
  }

  // Background: generate example sentences (don't block word creation)
  import("./useDirectApi").then(({ generateExampleSentences }) => {
    generateExampleSentences(hebrewText, translation)
      .then((sentences) => updateWordSentences(userId, docRef.id, sentences))
      .catch((err) => console.warn("Background sentence generation failed:", err));
  });

  return docRef.id;
}

/**
 * Get all words for a user
 */
export async function getWords(
  userId: string,
  options?: {
    category?: string;
    orderByField?: "firstAddedAt" | "lastReviewedAt" | "priorityScore";
    limitCount?: number;
  }
): Promise<Word[]> {
  let q = query(
    userWordsCol(userId),
    where("deleted", "==", false)
  );

  if (options?.category) {
    q = query(q, where("categories", "array-contains", options.category));
  }

  // Note: Firestore requires indexes for compound queries with orderBy
  // For now, we'll sort client-side
  const snapshot = await getDocs(q);

  let words = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Word[];

  // Client-side sorting
  if (options?.orderByField === "priorityScore") {
    words.sort((a, b) => b.reviewStats.priorityScore - a.reviewStats.priorityScore);
  } else if (options?.orderByField === "lastReviewedAt") {
    words.sort((a, b) => {
      if (!a.lastReviewedAt) return 1;
      if (!b.lastReviewedAt) return -1;
      return (b.lastReviewedAt as any).toMillis() - (a.lastReviewedAt as any).toMillis();
    });
  } else {
    words.sort((a, b) => (b.firstAddedAt as any).toMillis() - (a.firstAddedAt as any).toMillis());
  }

  if (options?.limitCount) {
    words = words.slice(0, options.limitCount);
  }

  return words;
}

/**
 * Update a word's translation
 */
export async function updateTranslation(
  userId: string,
  wordId: string,
  language: SupportedLanguage,
  newText: string
): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);
  await updateDoc(wordRef, {
    [`translations.${language}`]: {
      text: newText,
      source: "user",
      verified: true,
    },
  });
}

/**
 * Soft delete a word and remove it from all study sets
 */
export async function deleteWord(wordId: string, userId: string): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);
  const wordSnap = await getDoc(wordRef);
  const wordCategories: string[] = wordSnap.data()?.categories || [];

  await updateDoc(wordRef, { deleted: true });

  // Decrement category counts
  for (const cat of wordCategories) {
    await incrementCategoryCount(userId, cat, -1);
  }

  // Remove from all study sets that contain this word
  const studySets = await getStudySets(userId);
  for (const studySet of studySets) {
    if (studySet.wordIds.includes(wordId)) {
      await removeWordFromStudySet(userId, studySet.id, wordId);
    }
  }
}

/**
 * Update word notes
 */
export async function updateWordNotes(userId: string, wordId: string, notes: string): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);
  await updateDoc(wordRef, { notes });
}

/**
 * Update word categories
 */
export async function updateWordCategories(
  userId: string,
  wordId: string,
  categories: string[]
): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);
  const wordSnap = await getDoc(wordRef);
  const oldCategories: string[] = wordSnap.data()?.categories || [];

  await updateDoc(wordRef, { categories });

  // Ensure all categories exist
  for (const cat of categories) {
    await ensureCategory(userId, cat);
  }

  // Update category counts for added/removed categories
  const added = categories.filter((c) => !oldCategories.includes(c));
  const removed = oldCategories.filter((c) => !categories.includes(c));
  for (const cat of added) await incrementCategoryCount(userId, cat, 1);
  for (const cat of removed) await incrementCategoryCount(userId, cat, -1);
}

/**
 * Update word grammar information (also stores current schema version)
 */
export async function updateWordGrammar(
  userId: string,
  wordId: string,
  wordType: WordType,
  nounInfo?: NounInfo,
  verbInfo?: VerbInfo,
  adjectiveInfo?: AdjectiveInfo
): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);
  const updates: Record<string, any> = {
    wordType,
    grammarSchemaVersion: GRAMMAR_SCHEMA_VERSION,
  };

  if (nounInfo) {
    updates.nounInfo = nounInfo;
  }
  if (verbInfo) {
    updates.verbInfo = verbInfo;
  }
  if (adjectiveInfo) {
    updates.adjectiveInfo = adjectiveInfo;
  }

  await updateDoc(wordRef, updates);
}

/**
 * Get grammar from global cache, or analyze via API and cache the result.
 * The grammar collection is shared across all users since grammar is objective data.
 * Only uses cache if schemaVersion matches current GRAMMAR_SCHEMA_VERSION.
 */
export async function getOrAnalyzeGrammar(hebrewText: string): Promise<GrammarAnalysis | undefined> {
  const normalized = stripNiqqud(hebrewText);
  const cacheRef = doc(db, "grammar", normalized);

  // Check global cache first - only use if schema version matches
  try {
    const cached = await getDoc(cacheRef);
    if (cached.exists()) {
      const data = cached.data();
      // Only use cache if schema version matches current version
      if (data.schemaVersion === GRAMMAR_SCHEMA_VERSION) {
        return { wordType: data.wordType, nounInfo: data.nounInfo, verbInfo: data.verbInfo, adjectiveInfo: data.adjectiveInfo };
      }
      // Outdated schema - fall through to re-analyze
    }
  } catch (err) {
    // Cache read failed (e.g., rules not deployed yet) — fall through to API
    console.warn("Grammar cache read failed, falling back to API:", err);
  }

  // Not cached or outdated — analyze via API and cache the result
  try {
    const grammar = await analyzeWordGrammar(hebrewText);
    // Try to cache, but don't fail if cache write fails
    // Strip undefined fields — Firestore rejects them
    const cacheData: Record<string, any> = {
      wordType: grammar.wordType,
      schemaVersion: GRAMMAR_SCHEMA_VERSION,
      createdAt: Timestamp.now(),
    };
    if (grammar.nounInfo) cacheData.nounInfo = grammar.nounInfo;
    if (grammar.verbInfo) cacheData.verbInfo = grammar.verbInfo;
    if (grammar.adjectiveInfo) cacheData.adjectiveInfo = grammar.adjectiveInfo;
    try {
      await setDoc(cacheRef, cacheData);
    } catch (cacheErr) {
      console.warn("Grammar cache write failed:", cacheErr);
    }
    return grammar;
  } catch (err) {
    console.error("Grammar analysis failed:", hebrewText, err);
    return undefined;
  }
}

/**
 * Force re-analyze grammar and update both global cache and user's word.
 * Used when user triggers a grammar update (either manually or auto-update for outdated schema).
 */
export async function reanalyzeAndUpdateGrammar(
  userId: string,
  wordId: string,
  hebrewText: string
): Promise<GrammarAnalysis | undefined> {
  const normalized = stripNiqqud(hebrewText);
  const cacheRef = doc(db, "grammar", normalized);

  try {
    // Always call API for fresh analysis
    const grammar = await analyzeWordGrammar(hebrewText);

    // Update global cache
    const cacheData: Record<string, any> = {
      wordType: grammar.wordType,
      schemaVersion: GRAMMAR_SCHEMA_VERSION,
      createdAt: Timestamp.now(),
    };
    if (grammar.nounInfo) cacheData.nounInfo = grammar.nounInfo;
    if (grammar.verbInfo) cacheData.verbInfo = grammar.verbInfo;
    if (grammar.adjectiveInfo) cacheData.adjectiveInfo = grammar.adjectiveInfo;

    try {
      await setDoc(cacheRef, cacheData);
    } catch (cacheErr) {
      console.warn("Grammar cache write failed:", cacheErr);
    }

    // Update user's word document
    const wordRef = userWordDoc(userId, wordId);
    const wordUpdates: Record<string, any> = {
      wordType: grammar.wordType,
      grammarSchemaVersion: GRAMMAR_SCHEMA_VERSION,
    };
    if (grammar.nounInfo) wordUpdates.nounInfo = grammar.nounInfo;
    if (grammar.verbInfo) wordUpdates.verbInfo = grammar.verbInfo;
    if (grammar.adjectiveInfo) wordUpdates.adjectiveInfo = grammar.adjectiveInfo;

    await updateDoc(wordRef, wordUpdates);

    return grammar;
  } catch (err) {
    console.error("Grammar re-analysis failed:", hebrewText, err);
    return undefined;
  }
}

// ===========================================
// Review (FSRS - Free Spaced Repetition Scheduler)
// ===========================================

/**
 * Fill in default FSRS fields for words that predate the FSRS migration.
 */
function normalizeSrsFields(word: Word): Word {
  return {
    ...word,
    reviewStats: {
      ...word.reviewStats,
      stability: word.reviewStats.stability ?? 0,
      difficulty: word.reviewStats.difficulty ?? 0,
      elapsedDays: word.reviewStats.elapsedDays ?? 0,
      scheduledDays: word.reviewStats.scheduledDays ?? 0,
      reps: word.reviewStats.reps ?? 0,
      lapses: word.reviewStats.lapses ?? 0,
      state: word.reviewStats.state ?? 0,
      nextReviewAt: word.reviewStats.nextReviewAt ?? null,
      lastReview: word.reviewStats.lastReview ?? null,
    },
  };
}

/**
 * Reconstruct a ts-fsrs Card object from stored ReviewStats.
 */
function reviewStatsToCard(stats: Word["reviewStats"]): Card {
  const card = createEmptyCard();
  card.stability = stats.stability ?? 0;
  card.difficulty = stats.difficulty ?? 0;
  card.elapsed_days = stats.elapsedDays ?? 0;
  card.scheduled_days = stats.scheduledDays ?? 0;
  card.reps = stats.reps ?? 0;
  card.lapses = stats.lapses ?? 0;
  card.state = (stats.state ?? 0) as State;
  if (stats.nextReviewAt) {
    const due = (stats.nextReviewAt as any).toDate?.() ?? new Date(stats.nextReviewAt);
    card.due = due;
  }
  if (stats.lastReview) {
    const lr = (stats.lastReview as any).toDate?.() ?? new Date(stats.lastReview);
    card.last_review = lr;
  }
  return card;
}

/**
 * Get all 4 FSRS scheduling options for a word (for interval preview).
 */
export function getFsrsSchedulingOptions(stats: Word["reviewStats"]): Record<ReviewRating, { card: Card; interval: string }> {
  const card = reviewStatsToCard(stats);
  const now = new Date();
  const scheduling = fsrs.repeat(card, now);

  function formatInterval(days: number): string {
    if (days < 1) {
      const mins = Math.round(days * 24 * 60);
      return mins <= 0 ? "now" : `${mins}m`;
    }
    if (days < 30) return `${Math.round(days)}d`;
    if (days < 365) return `${Math.round(days / 30)}mo`;
    return `${(days / 365).toFixed(1)}y`;
  }

  const s = scheduling as unknown as Record<number, { card: Card }>;
  return {
    1: { card: s[1].card, interval: formatInterval(s[1].card.scheduled_days) },
    2: { card: s[2].card, interval: formatInterval(s[2].card.scheduled_days) },
    3: { card: s[3].card, interval: formatInterval(s[3].card.scheduled_days) },
    4: { card: s[4].card, interval: formatInterval(s[4].card.scheduled_days) },
  };
}

/**
 * Check if a word is due for review.
 * Words with nextReviewAt null (never reviewed) are always due.
 */
function isWordDue(word: Word): boolean {
  const nextReview = word.reviewStats.nextReviewAt;
  if (!nextReview) return true;
  const nextReviewMs = (nextReview as any).toMillis?.() ?? new Date(nextReview).getTime();
  return nextReviewMs <= Date.now();
}

/**
 * Get a batch of due words for review (SRS-based)
 */
export async function getReviewBatch(
  userId: string,
  batchSize: number,
  category?: string
): Promise<Word[]> {
  const allWords = await getWords(userId, { category });
  const words = allWords.map(normalizeSrsFields);

  // Filter to due words only
  const dueWords = words.filter(isWordDue);

  // Sort: most overdue first (null = never reviewed = highest priority)
  dueWords.sort((a, b) => {
    const aTime = a.reviewStats.nextReviewAt
      ? ((a.reviewStats.nextReviewAt as any).toMillis?.() ?? new Date(a.reviewStats.nextReviewAt).getTime())
      : 0;
    const bTime = b.reviewStats.nextReviewAt
      ? ((b.reviewStats.nextReviewAt as any).toMillis?.() ?? new Date(b.reviewStats.nextReviewAt).getTime())
      : 0;
    return aTime - bTime;
  });

  // Take up to batchSize, then shuffle for varied review order
  const batch = dueWords.slice(0, batchSize);
  for (let i = batch.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [batch[i], batch[j]] = [batch[j], batch[i]];
  }

  return batch;
}

/**
 * FSRS rating constants for readability
 */
export const FSRS_RATING = {
  Again: 1 as ReviewRating,
  Hard: 2 as ReviewRating,
  Good: 3 as ReviewRating,
  Easy: 4 as ReviewRating,
};

/**
 * Map ReviewRating to ts-fsrs Rating enum
 */
function toFsrsRating(rating: ReviewRating): Rating {
  const map: Record<ReviewRating, Rating> = {
    1: Rating.Again,
    2: Rating.Hard,
    3: Rating.Good,
    4: Rating.Easy,
  };
  return map[rating];
}

/**
 * Update review result using FSRS scheduling
 */
export async function updateReviewResult(
  userId: string,
  wordId: string,
  rating: ReviewRating,
  currentStats: Word["reviewStats"]
): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);

  // Reconstruct FSRS card from stored state
  const card = reviewStatsToCard(currentStats);
  const now = new Date();

  // Run FSRS scheduling
  const scheduling = fsrs.repeat(card, now);
  const s = scheduling as unknown as Record<number, { card: Card }>;
  const updatedCard = s[toFsrsRating(rating)].card;

  // Again = unknown, Hard/Good/Easy = known
  const isKnown = rating >= 2;
  const newKnownCount = currentStats.knownCount + (isKnown ? 1 : 0);
  const newUnknownCount = currentStats.unknownCount + (isKnown ? 0 : 1);

  await updateDoc(wordRef, {
    lastReviewedAt: Timestamp.now(),
    "reviewStats.knownCount": newKnownCount,
    "reviewStats.unknownCount": newUnknownCount,
    "reviewStats.lastResult": isKnown ? "known" : "unknown",
    "reviewStats.stability": updatedCard.stability,
    "reviewStats.difficulty": updatedCard.difficulty,
    "reviewStats.elapsedDays": updatedCard.elapsed_days,
    "reviewStats.scheduledDays": updatedCard.scheduled_days,
    "reviewStats.reps": updatedCard.reps,
    "reviewStats.lapses": updatedCard.lapses,
    "reviewStats.state": updatedCard.state as number,
    "reviewStats.nextReviewAt": Timestamp.fromDate(updatedCard.due),
    "reviewStats.lastReview": Timestamp.fromDate(now),
  });
}

/**
 * Get review stats including SRS due count
 */
export async function getReviewStats(
  userId: string,
  category?: string
): Promise<{
  totalWords: number;
  reviewedWords: number;
  totalKnown: number;
  totalUnknown: number;
  averageSuccessRate: number;
  dueCount: number;
  nextDueAt: Date | null;
}> {
  const rawWords = await getWords(userId, { category });
  const words = rawWords.map(normalizeSrsFields);

  let reviewedWords = 0;
  let totalKnown = 0;
  let totalUnknown = 0;
  let dueCount = 0;
  let nextDueAt: Date | null = null;

  for (const word of words) {
    const reviewed = word.reviewStats.knownCount + word.reviewStats.unknownCount;
    if (reviewed > 0) {
      reviewedWords++;
      totalKnown += word.reviewStats.knownCount;
      totalUnknown += word.reviewStats.unknownCount;
    }

    // Count due words
    if (isWordDue(word)) {
      dueCount++;
    } else {
      // Track earliest upcoming review
      const nextReviewMs = (word.reviewStats.nextReviewAt as any)?.toMillis?.()
        ?? (word.reviewStats.nextReviewAt ? new Date(word.reviewStats.nextReviewAt).getTime() : null);
      if (nextReviewMs && (!nextDueAt || nextReviewMs < nextDueAt.getTime())) {
        nextDueAt = new Date(nextReviewMs);
      }
    }
  }

  const totalReviews = totalKnown + totalUnknown;

  return {
    totalWords: words.length,
    reviewedWords,
    totalKnown,
    totalUnknown,
    averageSuccessRate: totalReviews > 0 ? totalKnown / totalReviews : 0,
    dueCount,
    nextDueAt,
  };
}

// ===========================================
// Categories
// ===========================================

/**
 * Ensure a category exists, create if not.
 * Uses an in-memory cache to avoid repeated Firestore queries for the same category.
 */
const knownCategories = new Map<string, Set<string>>();

async function ensureCategory(userId: string, name: string): Promise<void> {
  // Check in-memory cache first
  if (!knownCategories.has(userId)) {
    knownCategories.set(userId, new Set());
  }
  const userCache = knownCategories.get(userId)!;
  if (userCache.has(name)) return;

  const q = query(
    userCategoriesCol(userId),
    where("name", "==", name)
  );
  const existing = await getDocs(q);

  if (existing.empty) {
    await addDoc(userCategoriesCol(userId), {
      name,
      createdAt: Timestamp.now(),
      wordCount: 0,
    });
  }

  userCache.add(name);
}

/**
 * Increment or decrement a category's wordCount by delta.
 */
async function incrementCategoryCount(userId: string, categoryName: string, delta: number): Promise<void> {
  const q = query(userCategoriesCol(userId), where("name", "==", categoryName));
  const snap = await getDocs(q);
  if (!snap.empty) {
    await updateDoc(snap.docs[0].ref, { wordCount: increment(delta) });
  }
}

/**
 * Get all categories for a user
 */
export async function getCategories(userId: string): Promise<Category[]> {
  const snapshot = await getDocs(query(userCategoriesCol(userId)));

  const categories = snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  })) as Category[];

  // One-time backfill: recalculate counts if not yet synced
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.data()?.categoryCountsSynced) {
    const words = await getWords(userId);
    for (const cat of categories) {
      const count = words.filter((w) => w.categories.includes(cat.name)).length;
      if (count !== cat.wordCount) {
        cat.wordCount = count;
        await updateDoc(doc(db, "users", userId, "categories", cat.id), { wordCount: count });
      }
    }
    await setDoc(userRef, { categoryCountsSynced: true }, { merge: true });
  }

  return categories.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Create a category
 */
export async function createCategory(userId: string, name: string): Promise<Category> {
  const docRef = await addDoc(userCategoriesCol(userId), {
    name,
    createdAt: Timestamp.now(),
    wordCount: 0,
  });

  return {
    id: docRef.id,
    name,
    createdAt: new Date(),
    wordCount: 0,
  };
}

/**
 * Delete a category
 */
export async function deleteCategory(userId: string, categoryId: string, categoryName: string): Promise<void> {
  // Remove category from all words
  const words = await getWords(userId);
  for (const word of words) {
    if (word.categories.includes(categoryName)) {
      const wordRef = userWordDoc(userId, word.id);
      await updateDoc(wordRef, {
        categories: arrayRemove(categoryName),
      });
    }
  }

  // Delete the category
  await deleteDoc(userCategoryDoc(userId, categoryId));

  // Evict from the ensureCategory cache — otherwise re-adding a word with
  // this category in the same session skips recreating the category doc,
  // and the word's category silently disappears from all category lists.
  knownCategories.get(userId)?.delete(categoryName);
}

// ===========================================
// Study Sets
// ===========================================

/**
 * Create a new study set
 */
export async function createStudySet(
  userId: string,
  name: string,
  description?: string
): Promise<StudySet> {
  const now = Timestamp.now();
  const docRef = await addDoc(userStudySetsCol(userId), {
    name,
    description: description || "",
    wordIds: [],
    createdAt: now,
    updatedAt: now,
  });

  return {
    id: docRef.id,
    name,
    description: description || "",
    wordIds: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Get all study sets for a user with accurate word counts
 */
export async function getStudySets(userId: string): Promise<StudySet[]> {
  const q = query(
    userStudySetsCol(userId)
  );
  const snapshot = await getDocs(q);

  const studySets = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as StudySet[];

  // Get all non-deleted words to filter out stale IDs
  const words = await getWords(userId);
  const validWordIds = new Set(words.map((w) => w.id));

  // Filter wordIds to only include existing words
  for (const studySet of studySets) {
    studySet.wordIds = studySet.wordIds.filter((id) => validWordIds.has(id));
  }

  return studySets.sort((a, b) =>
    (b.updatedAt as any)?.toMillis?.() - (a.updatedAt as any)?.toMillis?.() || 0
  );
}

/**
 * Get a single study set by ID
 */
export async function getStudySet(userId: string, studySetId: string): Promise<StudySet | null> {
  const docRef = userStudySetDoc(userId, studySetId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as StudySet;
}

/**
 * Update a study set
 */
export async function updateStudySet(
  userId: string,
  studySetId: string,
  updates: { name?: string; description?: string }
): Promise<void> {
  const studySetRef = userStudySetDoc(userId, studySetId);
  await updateDoc(studySetRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a study set (words remain in word bank)
 */
export async function deleteStudySet(userId: string, studySetId: string): Promise<void> {
  await deleteDoc(userStudySetDoc(userId, studySetId));
}

/**
 * Add a word to a study set
 */
export async function addWordToStudySet(userId: string, studySetId: string, wordId: string): Promise<void> {
  const studySetRef = userStudySetDoc(userId, studySetId);
  await updateDoc(studySetRef, {
    wordIds: arrayUnion(wordId),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Remove a word from a study set
 */
export async function removeWordFromStudySet(userId: string, studySetId: string, wordId: string): Promise<void> {
  const studySetRef = userStudySetDoc(userId, studySetId);
  await updateDoc(studySetRef, {
    wordIds: arrayRemove(wordId),
    updatedAt: Timestamp.now(),
  });
}

/**
 * Clean up orphaned word IDs from all study sets
 * Removes references to words that have been deleted
 */
export async function cleanupStudySets(userId: string): Promise<number> {
  const studySets = await getStudySets(userId);
  const words = await getWords(userId);
  const validWordIds = new Set(words.map((w) => w.id));

  let removedCount = 0;

  for (const studySet of studySets) {
    // Get the raw study set to see original wordIds
    const studySetDocSnap = await getDoc(userStudySetDoc(userId, studySet.id));
    const rawWordIds = studySetDocSnap.data()?.wordIds || [];
    const orphanedIds = rawWordIds.filter((id: string) => !validWordIds.has(id));

    if (orphanedIds.length > 0) {
      const studySetRef = userStudySetDoc(userId, studySet.id);
      await updateDoc(studySetRef, {
        wordIds: rawWordIds.filter((id: string) => validWordIds.has(id)),
        updatedAt: Timestamp.now(),
      });
      removedCount += orphanedIds.length;
      console.log(`Removed ${orphanedIds.length} orphaned word IDs from "${studySet.name}"`);
    }
  }

  return removedCount;
}

/**
 * Get words in a study set
 */
export async function getStudySetWords(userId: string, studySetId: string): Promise<Word[]> {
  const studySet = await getStudySet(userId, studySetId);
  if (!studySet || studySet.wordIds.length === 0) {
    return [];
  }

  const allWords = await getWords(userId);
  return allWords.filter((word) => studySet.wordIds.includes(word.id));
}

/**
 * Get review batch from a study set (SRS-based)
 */
export async function getStudySetReviewBatch(
  userId: string,
  studySetId: string,
  batchSize: number
): Promise<Word[]> {
  const rawWords = await getStudySetWords(userId, studySetId);
  const words = rawWords.map(normalizeSrsFields);

  // Filter to due words only
  const dueWords = words.filter(isWordDue);

  // Sort: most overdue first
  dueWords.sort((a, b) => {
    const aTime = a.reviewStats.nextReviewAt
      ? ((a.reviewStats.nextReviewAt as any).toMillis?.() ?? new Date(a.reviewStats.nextReviewAt).getTime())
      : 0;
    const bTime = b.reviewStats.nextReviewAt
      ? ((b.reviewStats.nextReviewAt as any).toMillis?.() ?? new Date(b.reviewStats.nextReviewAt).getTime())
      : 0;
    return aTime - bTime;
  });

  // Take up to batchSize, then shuffle
  const batch = dueWords.slice(0, batchSize);
  for (let i = batch.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [batch[i], batch[j]] = [batch[j], batch[i]];
  }

  return batch;
}

// ===========================================
// Migration: Analyze Grammar for All Words
// ===========================================

/**
 * Clear grammar data from all words (to re-run migration)
 */
export async function clearAllGrammarData(userId: string): Promise<number> {
  const words = await getWords(userId);
  const wordsWithGrammar = words.filter((w) => w.wordType);

  console.log(`Clearing grammar from ${wordsWithGrammar.length} words...`);

  for (const word of wordsWithGrammar) {
    const wordRef = userWordDoc(userId, word.id);
    await updateDoc(wordRef, {
      wordType: null,
      nounInfo: null,
      verbInfo: null,
      adjectiveInfo: null,
    });
  }

  console.log(`Cleared grammar from ${wordsWithGrammar.length} words`);
  return wordsWithGrammar.length;
}

/**
 * One-time migration to analyze grammar for all existing words
 * Run from browser console:
 *   import { migrateWordsGrammar } from './hooks/useFirestore';
 *   migrateWordsGrammar('YOUR_USER_ID', (progress) => console.log(progress));
 */
export async function migrateWordsGrammar(
  userId: string,
  onProgress?: (progress: { current: number; total: number; word: string }) => void
): Promise<{ analyzed: number; skipped: number; errors: number }> {
  // Dynamic import to avoid circular dependency
  const { analyzeWordGrammar } = await import("./useDirectApi");

  const words = await getWords(userId);
  const wordsToAnalyze = words.filter((w) => !w.wordType);

  const results = { analyzed: 0, skipped: words.length - wordsToAnalyze.length, errors: 0 };

  console.log(`Starting grammar migration: ${wordsToAnalyze.length} words to analyze, ${results.skipped} already have grammar`);

  for (let i = 0; i < wordsToAnalyze.length; i++) {
    const word = wordsToAnalyze[i];

    if (onProgress) {
      onProgress({ current: i + 1, total: wordsToAnalyze.length, word: word.hebrew.text });
    }

    try {
      const grammar = await analyzeWordGrammar(word.hebrew.text);
      await updateWordGrammar(userId, word.id, grammar.wordType, grammar.nounInfo, grammar.verbInfo, grammar.adjectiveInfo);
      results.analyzed++;
      console.log(`[${i + 1}/${wordsToAnalyze.length}] Analyzed: ${word.hebrew.text} -> ${grammar.wordType}`);
    } catch (error) {
      console.error(`[${i + 1}/${wordsToAnalyze.length}] Error analyzing ${word.hebrew.text}:`, error);
      results.errors++;
    }

    // Small delay to avoid rate limiting
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  console.log(`Migration complete:`, results);
  return results;
}

// ===========================================
// User Preferences
// ===========================================

export interface UserPreferences {
  nativeLanguage: NativeLanguage;
  defaultTranslationDirection: TranslationDirection;
  autoCategories: boolean;
  gender?: "male" | "female";
  onboardingCompleted: boolean;
  walkthroughCompleted?: boolean;
  hebrewLevel?: HebrewLevel;
  learningGoal?: LearningGoal;
  onboardingPrompt?: string;
  dailyActivities?: string[];
  interests?: string[];
}

const DEFAULT_PREFERENCES: UserPreferences = {
  nativeLanguage: "en",
  defaultTranslationDirection: "he-en",
  autoCategories: true,
  gender: undefined,
  onboardingCompleted: false,
};

export async function getUserPreferences(
  userId: string,
  authProfile?: { displayName?: string | null; email?: string | null }
): Promise<UserPreferences> {
  const userRef = doc(db, "users", userId);
  const snapshot = await getDoc(userRef);

  const profileFields: Record<string, string> = {};
  if (authProfile?.displayName) profileFields.displayName = authProfile.displayName;
  if (authProfile?.email) profileFields.email = authProfile.email;

  if (!snapshot.exists()) {
    // Create user doc with defaults if it doesn't exist
    try {
      await setDoc(userRef, {
        uid: userId,
        ...profileFields,
        ...DEFAULT_PREFERENCES,
        onboardingCompleted: false,
        plan: "free",
        createdAt: Timestamp.now(),
      });
    } catch (err) {
      console.error("Failed to create user doc, returning defaults:", err);
    }
    return DEFAULT_PREFERENCES;
  }

  const data = snapshot.data();

  // Backfill missing core fields
  const backfill: Record<string, any> = {};
  if (!data.nativeLanguage) backfill.nativeLanguage = DEFAULT_PREFERENCES.nativeLanguage;
  if (!data.defaultTranslationDirection) backfill.defaultTranslationDirection = DEFAULT_PREFERENCES.defaultTranslationDirection;
  if (data.autoCategories === undefined) backfill.autoCategories = DEFAULT_PREFERENCES.autoCategories;
  if (!data.uid) backfill.uid = userId;
  if (!data.plan) backfill.plan = "free";

  try {
    if (Object.keys(backfill).length > 0) {
      await setDoc(userRef, { ...profileFields, ...backfill }, { merge: true });
    } else if (Object.keys(profileFields).length > 0) {
      await setDoc(userRef, profileFields, { merge: true });
    }
  } catch (err) {
    console.error("Failed to backfill user preferences:", err);
  }

  return {
    nativeLanguage: data.nativeLanguage || DEFAULT_PREFERENCES.nativeLanguage,
    defaultTranslationDirection: data.defaultTranslationDirection || DEFAULT_PREFERENCES.defaultTranslationDirection,
    autoCategories: data.autoCategories ?? DEFAULT_PREFERENCES.autoCategories,
    gender: data.gender,
    onboardingCompleted: data.onboardingCompleted ?? false,
    hebrewLevel: data.hebrewLevel,
    learningGoal: data.learningGoal,
    onboardingPrompt: data.onboardingPrompt,
    dailyActivities: data.dailyActivities,
    interests: data.interests,
    walkthroughCompleted: data.walkthroughCompleted,
  };
}

export async function updateUserPreferences(
  userId: string,
  prefs: Partial<UserPreferences>
): Promise<void> {
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, prefs);
}

// ===========================================
// Streaks
// ===========================================

/**
 * Local-time "YYYY-MM-DD" key for streak day boundaries. Using local time
 * (not UTC) so a review at 8pm counts for the user's own calendar day.
 */
function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Update the user's review streak after completing a review session.
 * Call this when a review session finishes (at least 1 card reviewed).
 */
export async function updateStreak(userId: string): Promise<StreakData> {
  const userRef = doc(db, "users", userId);
  const snapshot = await getDoc(userRef);
  const data = snapshot.data();

  const today = localDateKey(new Date());
  const current: StreakData = data?.streakData ?? {
    currentStreak: 0,
    longestStreak: 0,
    lastReviewDate: "",
  };

  // Already reviewed today — no change
  if (current.lastReviewDate === today) {
    return current;
  }

  // Check if yesterday
  const yesterday = localDateKey(new Date(Date.now() - 86400000));
  let newStreak: number;

  if (current.lastReviewDate === yesterday) {
    newStreak = current.currentStreak + 1;
  } else {
    newStreak = 1;
  }

  const updated: StreakData = {
    currentStreak: newStreak,
    longestStreak: Math.max(newStreak, current.longestStreak),
    lastReviewDate: today,
  };

  await updateDoc(userRef, { streakData: updated });
  return updated;
}

/**
 * Get the user's current streak data.
 */
export async function getStreakData(userId: string): Promise<StreakData> {
  const userRef = doc(db, "users", userId);
  const snapshot = await getDoc(userRef);
  const data = snapshot.data();

  const streakData: StreakData = data?.streakData ?? {
    currentStreak: 0,
    longestStreak: 0,
    lastReviewDate: "",
  };

  // Check if streak is still active (reviewed today or yesterday)
  const today = localDateKey(new Date());
  const yesterday = localDateKey(new Date(Date.now() - 86400000));

  if (streakData.lastReviewDate !== today && streakData.lastReviewDate !== yesterday) {
    // Streak has lapsed
    return { ...streakData, currentStreak: 0 };
  }

  return streakData;
}

// ===========================================
// Example Sentences
// ===========================================

/**
 * Update a word's example sentences
 */
export async function updateWordSentences(
  userId: string,
  wordId: string,
  sentences: Array<{ hebrew: string; english: string }>
): Promise<void> {
  const wordRef = userWordDoc(userId, wordId);
  await updateDoc(wordRef, { exampleSentences: sentences });
}

// ===========================================
// Onboarding Types
// ===========================================

export interface OnboardingWord {
  hebrew: string;
  english: string;
  category: string;
}
